// Import order is load-bearing: tokens define the variables, the reset clears
// the browser's defaults, and global styles use both. A CSS module imported by
// a component always lands after these, so a module can safely override them.
import '@/styles/tokens.css';
import '@/styles/reset.css';
import '@/styles/global.css';

/**
 * The entry point, and deliberately almost empty.
 *
 * IT IMPORTS THE APP DYNAMICALLY so that a failure to boot is something a
 * person can read. `config/env` validates the environment as its module is
 * evaluated and THROWS when a variable is missing — which is the right call,
 * a misconfigured build must not boot half-working — but a throw during the
 * import graph leaves an empty <div id="root"> and a white screen, with the
 * reason only in the console. That is what a Netlify deploy with no
 * environment variables set looks like, and it is unreadable.
 *
 * Nothing above this line can throw: three CSS imports and no code. The app,
 * and with it the whole `config/env` chain, is behind the `import()` below,
 * inside a try/catch that paints the reason onto the page.
 */

const container = document.getElementById('root');

if (!container) {
  throw new Error('No #root element — check index.html.');
}

/*
 * Stale-chunk handling. Every route is a hashed, lazily-imported chunk; a new
 * deploy replaces every hash, so a tab left open before the deploy 404s the old
 * chunk. Vite fires `vite:preloadError` for that — including for SPECULATIVE
 * preloads of dependencies that the app hasn't navigated to yet. We only
 * suppress the noisy uncaught error here; we do NOT reload, because a background
 * preload failing must never yank the page out from under someone mid-action
 * (e.g. submitting a form). The real recovery lives at the navigation site:
 * `router/index.tsx`'s `lazyRoute` catches the failed import when the user
 * actually goes to a route whose chunk is gone, and reloads once there.
 */
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
});

void (async () => {
  try {
    const { mountApp } = await import('./bootstrap');
    mountApp(container);
  } catch (error) {
    renderFatal(container, error);
  }
})();

/**
 * The last-resort error screen.
 *
 * Built with `textContent` and DOM calls rather than `innerHTML`: the message
 * can carry a value straight out of the environment, and this is the one code
 * path that runs when everything else is broken — it must not be the thing
 * that introduces an injection. Inline styles because a stylesheet may be
 * exactly what failed.
 */
function renderFatal(container: HTMLElement, error: unknown): void {
  const message = error instanceof Error ? error.message : String(error);

  // Still log it: the stack is worth more than the message to whoever is
  // actually debugging, and the screen below only carries the message.
  console.error('[fibo] the app could not start', error);

  container.replaceChildren();

  /* This screen paints its OWN full-bleed ground instead of sitting on the
     page's. It has to: the values below are literals by design (a stylesheet
     may be exactly what failed, so tokens cannot be trusted here), and a
     literal palette that inherits an unknown background is a coin flip. It
     used to be near-black type on whatever was behind it, which was fine
     while light was the default and became black-on-black the day dark
     became it. Owning the ground makes it readable in both cases — tokens
     loaded or not. The values mirror the dark palette; they do not read it. */
  container.style.cssText = 'min-height:100vh;background:#000000;color:#f3f4f6;';

  const wrap = document.createElement('div');
  wrap.style.cssText =
    'max-width:46rem;margin:0 auto;padding:12vh 1.5rem;' +
    'font:16px/1.6 ui-sans-serif,system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;' +
    'color:#f3f4f6;';

  const heading = document.createElement('h1');
  heading.textContent = 'Fibo Markets could not start';
  heading.style.cssText = 'font-size:1.5rem;font-weight:650;margin:0 0 .75rem;';

  const lede = document.createElement('p');
  lede.textContent =
    'The app checks its configuration before it boots, and that check failed. ' +
    'Nothing is wrong with your browser.';
  lede.style.cssText = 'margin:0 0 1.25rem;color:#9ca3af;';

  const pre = document.createElement('pre');
  pre.textContent = message;
  pre.style.cssText =
    'margin:0 0 1.25rem;padding:1rem 1.25rem;border-radius:10px;' +
    'background:#171719;border:1px solid #2a2a2e;color:#f87171;' +
    'font:13px/1.5 ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;' +
    'white-space:pre-wrap;word-break:break-word;';

  const hint = document.createElement('p');
  hint.style.cssText = 'margin:0;color:#6b7280;font-size:.9375rem;';
  hint.textContent =
    'Running locally? Copy .env.example to .env.local and restart the dev server. ' +
    'Deployed? The VITE_* variables are inlined at build time — set them in your ' +
    'host (netlify.toml, or Site configuration → Environment variables) and redeploy.';

  wrap.append(heading, lede, pre, hint);
  container.append(wrap);
}
